/* eslint-disable jsx-a11y/label-has-associated-control */
import { useRef, useState } from 'react'
import { withTheme } from 'emotion-theming'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { withTranslation } from 'server/i18n'
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3'

import { submitNewsletterForm } from 'helpers/middleware'
import { isStorageSupported } from 'helpers/utils'

import ArrowIcon from 'public/icons/arrow.svg'

import style from './NewsletterSubscription.style'

const NewsletterSubscriptionStyled = styled.div(props => ({ ...style(props) }))

const validateEmail = input => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(input)

const languages = [
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' }
]

const NewsletterSubscription = ({ t, i18n, inverted, placeholder }) => {
    const [form, setForm] = useState({
        hp: '',
        email: '',
        language: i18n.language || languages[0].code,
        firstName: '',
        lastName: ''
    })
    const [errors, setErrors] = useState({
        email: false,
        language: false,
        firstName: false,
        lastName: false
    })
    const submitLock = useRef(false)

    const setFormValue = (key, value) => {
        setForm(state => ({
            ...state,
            [key]: value
        }))
        setErrors(state => ({
            ...state,
            [key]: false
        }))
    }

    const initialState = () => {
        if (process.browser && isStorageSupported(window, 'sessionStorage')) {
            if (sessionStorage.getItem('subscribed')) return true
        }
        return false
    }
    const [submitted, setSubmitted] = useState(initialState)

    const { executeRecaptcha } = useGoogleReCaptcha()

    const handleSubmit = async () => {
        if (submitted || submitLock.current) {
            return
        }

        const formErrors = {
            email: !form.email || !validateEmail(form.email),
            language: !form.language,
            firstName: !form.firstName,
            lastName: !form.lastName
        }

        setErrors(state => ({
            ...state,
            ...formErrors
        }))

        if (Object.values(formErrors).some(Boolean) || form.hp) {
            return
        }

        submitLock.current = true

        try {
            const token = executeRecaptcha ? await executeRecaptcha('newsletter_form') : null
            await submitNewsletterForm({ ...form, recaptchaToken: token })
            setSubmitted(true)
            if (process.browser && isStorageSupported(window, 'sessionStorage')) {
                sessionStorage.setItem('subscribed', 'true')
            }
        } catch (error) {
            console.error('[NewsletterSubscription] submit failed', error)
        } finally {
            submitLock.current = false
        }
    }

    return (
        <>
            <NewsletterSubscriptionStyled inverted={inverted}>
                {!submitted ? (
                    <>
                        <input
                            type="text"
                            name="hp"
                            value={form.hp}
                            onChange={e => setFormValue('hp', e.target.value)}
                            tabIndex="-1"
                            autoComplete="off"
                            aria-hidden="true"
                            style={{
                                position: 'absolute',
                                left: '-10000px',
                                top: 'auto',
                                width: '1px',
                                height: '1px',
                                overflow: 'hidden'
                            }}
                        />
                        <div className="row">
                            <input
                                type="text"
                                placeholder={t('common:name')}
                                className={errors.firstName ? 'has-error' : null}
                                value={form.firstName}
                                onChange={e => setFormValue('firstName', e.target.value)}
                            />
                            <input
                                type="text"
                                placeholder={t('common:last-name')}
                                className={errors.lastName ? 'has-error' : null}
                                value={form.lastName}
                                onChange={e => setFormValue('lastName', e.target.value)}
                            />
                        </div>
                        <div className="row">
                            <strong>{t('common:language')}</strong>
                            {languages.map(item => (
                                <label
                                    key={item.code}
                                    className={`radio${item.code === form.language ? ' checked' : ''}`}>
                                    <input
                                        type="radio"
                                        checked={item.code === form.language}
                                        onChange={() => setFormValue('language', item.code)}
                                    />
                                    {item.label}
                                </label>
                            ))}
                        </div>
                    </>
                ) : null}
                <div className="row">
                    {!submitted && (
                        <>
                            <input
                                type="email"
                                className={errors.email ? 'has-error' : null}
                                value={form.email}
                                disabled={submitted}
                                placeholder={placeholder}
                                onChange={e => setFormValue('email', e.target.value)}
                            />

                            <button type="button" className="NewsletterSubscriptionButton" onClick={handleSubmit}>
                                {t('sign-up')}
                                <span>
                                    <ArrowIcon />
                                </span>
                            </button>
                        </>
                    )}

                    {submitted && (
                        <div className="submitted">
                            <span>{t('thank-you-subscription')}</span>
                        </div>
                    )}
                </div>
            </NewsletterSubscriptionStyled>
        </>
    )
}

NewsletterSubscription.propTypes = {
    t: PropTypes.func.isRequired,
    i18n: PropTypes.oneOfType([() => null, PropTypes.object]).isRequired,
    inverted: PropTypes.bool,
    placeholder: PropTypes.string.isRequired
}

NewsletterSubscription.defaultProps = {
    inverted: false
}

const EnhancedNewsletter = withTranslation('controls', 'common')(withTheme(NewsletterSubscription))
const NewsletterWithCaptcha = props => (
    <GoogleReCaptchaProvider reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}>
        <EnhancedNewsletter {...props} />
    </GoogleReCaptchaProvider>
)
export default NewsletterWithCaptcha
